import { Box, Tooltip } from '@mui/material';
import React, { useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { setCurrentConversationId as setCurrentConversationIdAction } from '../../../actions/aiAssistant.actions';
import { campaign, externalLinks, onboardingHints } from '../../../constants';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../../services/track.service';
import { aiAssistantContext } from '../../../state/AIAssistantContextProvider';
import useAction from '../../../utils/useAction';
import AIButton from '../../AIButton';
import { OnboardingHint } from '../../OnboardingHint';
import { useReaderContext } from '../../Reader/ReaderContext';
import { ActiveView } from '../shared/constants';
import { MukiBudeOnboardingStep } from './hooks/useMukiBudeOnboarding';

interface AIAssistantTogglerProps {
	isClassic?: boolean;
	buttonType?: 'round' | 'rectangular';
}

const AIAssistantToggler = ({
	isClassic = false,
	buttonType = 'round',
}: AIAssistantTogglerProps) => {
	const { t } = useTranslation();
	const {
		isAssistantDisabled,
		isAssistantOpen,
		openAssistant,
		setActiveView,
		closeAssistant,
		onboardingState,
		concludeOnboardingStep,
	} = useContext(aiAssistantContext);
	const { mindItemId } = useReaderContext();
	const dismissHint = useRef<() => void | undefined>();
	const setCurrentConversationId = useAction(setCurrentConversationIdAction);
	const readerHintShouldShow =
		onboardingState?.[MukiBudeOnboardingStep.READER_HINT].shouldShow && !isAssistantOpen;

	const handleTogglerClick = () => {
		if (isAssistantOpen) {
			closeAssistant();
		} else {
			openAssistant(mindItemId);
		}
		dismissHint.current?.();
	};

	const handleReaderHintClick = () => {
		openAssistant(mindItemId);
		setActiveView(ActiveView.UNIT_ENTRY);
		setCurrentConversationId({ conversationId: null });
	};

	const setDismissHint = (fn: () => void) => {
		dismissHint.current = fn;
	};

	const renderToggler = () => {
		const tooltipTitle = isAssistantDisabled
			? t('AI assistant temporarily unavailable')
			: t('AI Assistant Muki');
		const tooltipPlacement = buttonType === 'round' ? 'left' : 'bottom';

		return (
			<Tooltip title={tooltipTitle} placement={tooltipPlacement} arrow>
				<Box sx={{ flexShrink: 0 }}>
					<AIButton
						isClassic={isClassic}
						isActive={isAssistantOpen}
						onClick={handleTogglerClick}
						disabled={isAssistantDisabled}
						buttonType={buttonType}
					/>
				</Box>
			</Tooltip>
		);
	};

	if (readerHintShouldShow) {
		return (
			<OnboardingHint
				placement="bottom-end"
				id={onboardingHints.aiAssistant.readerHint}
				title={t('Ideas for your material')}
				description={t(
					'Here, Muki will give you suitable suggestions for adapting your' +
						' unit. Choose from templates or ask your own question.'
				)}
				onClose={() => concludeOnboardingStep(MukiBudeOnboardingStep.READER_HINT)}
				isNew
				disablePortal={false}
				maxWidth={390}
				mobileMaxWidth={330}
				actions={{
					primary: {
						label: t('Try it out now!'),
						onClick: handleReaderHintClick,
					},
					secondary: {
						label: t('Learn more'),
						onClick: () => window.open(externalLinks.assistantFaq, '_blank'),
						withoutDismiss: true,
						buttonProps: {
							variant: 'text',
						},
					},
				}}
				externalDismiss={setDismissHint}
				onShow={() => {
					const walkthroughStep = 1;
					ampliTrackMessageDisplayedEvent(campaign.mukiHintAiButton, MessageType.HINT, walkthroughStep);
				}}
			>
				<Box sx={{ flexShrink: 0 }}>{renderToggler()}</Box>
			</OnboardingHint>
		);
	}

	return renderToggler();
};

export default AIAssistantToggler;
